import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { Phase, PreparationMethod, StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { Checkbox, Field, RadioGroup, TextOutput } from '../../../../../Core';
import { withInjectedProps } from '../../../../../Core/InjectedPropsContext';
import { ListFieldSection, PageSection } from '../../../../../Layout';
import { getLabelForPortionState, getNumberOfSeparations } from '../../../Helpers';

let SpecimenPreparationPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const stateBeforePreparation: StateOfMaterial = useSelector(
    createWorksheetSelector!('WholePortion.Preparation.StateBeforePreparation')
  );
  const isMaterialWashed = useSelector(
    pipe(createWorksheetSelector!('Preparation.MaterialWashed'), valueOrDefault(false))
  );
  const isInitialSeparationPerformed = useSelector(createWorksheetSelector!('Preparation.InitialSeparationPerformed'));

  const anySeparations = useSelector(pipe(createWorksheetSelector!('', false), getNumberOfSeparations, (n) => n > 0));

  const washingPhase = useSelector(createWorksheetSelector!('Preparation.WashingPhase')) as Phase;
  const materialWashedOptions: Array<{ label: string; value: Phase; disabled?: boolean }> = [
    { label: 'None', value: Phase.Never },
    {
      label: 'Whole specimen',
      value: Phase.WholePortion,
    },
    {
      label: 'After first separation',
      value: Phase.AfterFirstSeparation,
    },
  ];

  //The "As Received" option will be disabled if wet material was received but must be dried prior to testing.
  const isOvenDryingRequired =
    stateBeforePreparation === StateOfMaterial.Moist &&
    anySeparations &&
    !isInitialSeparationPerformed &&
    (washingPhase === Phase.Never || washingPhase === Phase.AfterFirstSeparation);

  return (
    <PageSection heading="Specimen Preparation">
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="WholePortion.Preparation.StateBeforePreparation"
          options={[
            { label: getLabelForPortionState(StateOfMaterial.Moist), value: StateOfMaterial.Moist },
            { label: getLabelForPortionState(StateOfMaterial.OvenDry), value: StateOfMaterial.OvenDry },
          ]}
          confirmText="Changing the condition of received sample may clear data. Are you sure you want to continue?"
        />
        {/* The checkbox for indicating washing will occur is an exception
            in this section in that is not mapped to the whole portion but the "test preparation" data
            as it indicates that washing will occur at some point during the whole test.
            (The washed portion will be the whole portion when no initial separation occurs,
               otherwise the first separation fine portion subspecimen)
          */}

        {anySeparations && !isInitialSeparationPerformed ? (
          <Field
            input={<RadioGroup />}
            label="Material Washed"
            name="Preparation.WashingPhase"
            options={materialWashedOptions}
            confirm={washingPhase !== Phase.Never}
            confirmTitle="Confirm Washing Selection"
            confirmText="This action will clear any entered portion preparation data. Are you sure you want to continue?"
          />
        ) : (
          <Field
            input={<Checkbox />}
            name="Preparation.MaterialWashed"
            confirmText="This action will clear any entered washing data. Are you sure you want to continue?"
            confirm={isMaterialWashed}
          />
        )}

        {stateBeforePreparation === StateOfMaterial.Moist ? (
          <Field
            input={<RadioGroup />}
            name="WholePortion.Preparation.PreparationMethod"
            options={[
              {
                label: 'As received',
                value: PreparationMethod.Moist,
                disabled: isOvenDryingRequired,
              },
              {
                label: 'Oven dry',
                value: PreparationMethod.OvenDry,
              },
              {
                label: 'Pre-treatment',
                value: PreparationMethod.PreTreated,
              },
            ]}
            confirmText="Changing the material preparation method may clear data. Are you sure you want to continue?"
          />
        ) : (
          <Field input={<TextOutput />} name="WholePortion.Preparation.PreparationMethod" value="As received" />
        )}
      </ListFieldSection>
    </PageSection>
  );
};

SpecimenPreparationPageSection = withInjectedProps(SpecimenPreparationPageSection);

export { SpecimenPreparationPageSection };
export default SpecimenPreparationPageSection;
